import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  LayoutDashboard, Users, Bell, Settings, Menu, X, LogOut,
  CheckCircle, Zap, TrendingUp, ShoppingCart, DollarSign, 
  Clock, Activity, ArrowUpRight, Search, Check, XCircle, MapPin, Phone,
  Mail, Building, FileText, Download, AlertCircle, Trash2, ChevronDown, ChevronUp,
  AlertTriangle, Loader2, BarChart2, CheckSquare, Square,
  User, Lock, Palette, Key, Shield, Eye, EyeOff, Save, RefreshCw,
  Monitor, Moon, Sun, Trash, MoreVertical, Circle,
  CreditCard, ExternalLink, RotateCcw,
  History, Headphones
} from 'lucide-react';

// Import API service
import api from './services/api';

// Notification Component
const Notification = ({ notification, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const notifConfig = {
    success: { icon: CheckCircle, bg: 'bg-green-50 border-green-200', iconColor: 'text-green-500' },
    error: { icon: XCircle, bg: 'bg-red-50 border-red-200', iconColor: 'text-red-500' },
    warning: { icon: AlertCircle, bg: 'bg-orange-50 border-orange-200', iconColor: 'text-orange-500' },
    info: { icon: Bell, bg: 'bg-blue-50 border-blue-200', iconColor: 'text-blue-500' }
  };

  const { icon: Icon, bg, iconColor } = notifConfig[notification.type];

  return (
    <div 
      className={`fixed top-4 right-4 z-[100] w-96 ${bg} border-2 rounded-xl p-4 shadow-2xl animate-slide-in cursor-pointer hover:shadow-3xl transition-all`}
      onClick={notification.onClick}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-6 h-6 flex-shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 mb-1">{notification.title}</h4>
          <p className="text-sm text-gray-700">{notification.message}</p>
          {notification.action && (
            <p className="text-xs text-gray-500 mt-2 italic">Click to view details</p>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="flex-shrink-0 hover:bg-black/5 rounded p-1 transition-colors">
          <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
        </button>
      </div>
    </div>
  );
};

// Confirmation Dialog Component
const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText, type = 'danger' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform scale-100 animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${type === 'danger' ? 'bg-red-100' : 'bg-orange-100'}`}>
              <AlertTriangle className={`w-6 h-6 ${type === 'danger' ? 'text-red-600' : 'text-orange-600'}`} />
            </div>
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          </div>
          <p className="text-gray-600 mb-6">{message}</p>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition-all">
              Cancel
            </button>
            <button
              onClick={() => { onConfirm(); onClose(); }}
              className={`flex-1 py-3 ${type === 'danger' ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'} text-white rounded-lg font-semibold transition-all hover:shadow-lg`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton Component
const SkeletonCard = () => (
  <div className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
      <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
    </div>
    <div className="w-24 h-4 bg-gray-200 rounded mb-2"></div>
    <div className="w-32 h-8 bg-gray-200 rounded mb-2"></div>
    <div className="w-28 h-3 bg-gray-200 rounded"></div>
  </div>
);

// Empty State Component
const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-12 h-12 text-gray-400" />
    </div>
    <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 text-center max-w-md">{description}</p>
  </div>
);

// Login Component
const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setIsLoading(true);
    try {
      const result = await api.login({
        email,
        password,
        userDeviceToken: 'web_device_token',
        userDeviceType: 'web'
      });
      
      if (result.success) {
        onLogin(result.data.adminDetail);
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    { icon: CheckCircle, title: 'Advanced Analytics', desc: 'Real-time dashboards with comprehensive business insights.' },
    { icon: Zap, title: 'Instant Verification', desc: 'Streamlined merchant onboarding with automated checks.' },
    { icon: TrendingUp, title: 'Growth Insights', desc: 'Monitor performance and identify opportunities.' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-800 to-gray-900 p-12 flex-col justify-between text-white">
        <div>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-7 h-7 text-black" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Helpsum</h1>
              <p className="text-sm text-gray-400">Enterprise Admin Platform</p>
            </div>
          </div>
          
          <p className="text-gray-300 text-lg mb-12">
            Streamline your business operations with our comprehensive admin platform.
          </p>
          
          <div className="space-y-6">
            {features.map((feature, i) => (
              <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:bg-gray-800/70 transition-colors">
                <feature.icon className="w-8 h-8 text-yellow-400 mb-3" />
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-8 text-sm text-gray-400">
          <span>99.9% Uptime</span>
          <span>10k+ Merchants</span>
          <span>24/7 Support</span>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Welcome Back</h2>
            <p className="text-gray-600 mb-8">Sign in to access your admin dashboard</p>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@helpsum.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-shadow"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 transition-shadow"
                  disabled={isLoading}
                />
              </div>
              
              <button
                type="submit"
                disabled={!email || !password || isLoading}
                className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  'Sign In to Dashboard'
                )}
              </button>
              
              <p className="text-center text-sm text-gray-500">Demo: Use any email and password to login</p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ sidebarOpen, setSidebarOpen, onLogout }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  const menuItems = [
    { id: 'dashboard', path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'merchants', path: '/merchants', icon: Users, label: 'Merchants' },
    { id: 'transactions', path: '/transactions', icon: CreditCard, label: 'Transactions' },
    { id: 'support', path: '/support', icon: Headphones, label: 'Support Tickets' },
    { id: 'audit-logs', path: '/audit-logs', icon: History, label: 'Audit Logs' },
    { id: 'notifications', path: '/notifications', icon: Bell, label: 'Notifications' },
    { id: 'settings', path: '/settings', icon: Settings, label: 'Settings' },
    { id: 'privacyPolicy', path: '/privacy-policy', icon: FileText, label: 'Privacy Policy' },
    { id: 'termsOfService', path: '/terms-of-service', icon: Shield, label: 'Terms of Service' },
    { id: 'deleteAccount', path: '/delete-account', icon: Trash2, label: 'Delete Account' }
  ];

  const handleLogout = () => {
    api.logout();
    onLogout();
  };

  return (
    <>
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:static inset-y-0 left-0 z-50 w-80 bg-white border-r border-gray-200 transition-transform flex flex-col shadow-lg md:shadow-none`}>
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-black" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Helpsum</h1>
                <p className="text-xs text-gray-500">Admin Panel</p>
              </div>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="md:hidden hover:bg-gray-100 p-2 rounded-lg transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {menuItems.map(item => {
              const isActive = currentPath === item.path || (currentPath === '/' && item.path === '/dashboard');
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive
                      ? 'bg-yellow-400 text-black font-semibold shadow-md' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
        
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
      
      {sidebarOpen && <div onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-black/50 z-40 md:hidden" />}
    </>
  );
};

// TRANSACTION MANAGEMENT HUB COMPONENT
const TransactionManagement = ({ addNotification }) => {
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', transaction: null });
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  useEffect(() => {
    const loadTransactions = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockTransactions = [
        {
          id: 'TXN-2024-001',
          merchant: 'Tech Haven Store',
          customer: 'John Doe',
          amount: 245.50,
          status: 'completed',
          type: 'payment',
          date: '2024-01-28 14:32',
          paymentMethod: 'Credit Card',
          cardLast4: '4242',
          description: 'Service booking payment',
          refundable: true
        },
        {
          id: 'TXN-2024-002',
          merchant: 'Fashion Boutique',
          customer: 'Jane Smith',
          amount: 89.99,
          status: 'pending',
          type: 'payment',
          date: '2024-01-28 13:15',
          paymentMethod: 'PayPal',
          cardLast4: null,
          description: 'Product purchase',
          refundable: false
        },
        {
          id: 'TXN-2024-003',
          merchant: 'Gourmet Kitchen',
          customer: 'Mike Johnson',
          amount: 150.00,
          status: 'refunded',
          type: 'refund',
          date: '2024-01-27 16:45',
          paymentMethod: 'Credit Card',
          cardLast4: '5678',
          description: 'Full refund - cancelled service',
          refundable: false,
          refundedAmount: 150.00,
          refundDate: '2024-01-28 09:20'
        },
        {
          id: 'TXN-2024-004',
          merchant: 'Digital Solutions',
          customer: 'Sarah Williams',
          amount: 2450.00,
          status: 'completed',
          type: 'payment',
          date: '2024-01-27 11:22',
          paymentMethod: 'Bank Transfer',
          cardLast4: null,
          description: 'Consulting services',
          refundable: true
        },
        {
          id: 'TXN-2024-005',
          merchant: 'Urban Style',
          customer: 'David Brown',
          amount: 67.50,
          status: 'failed',
          type: 'payment',
          date: '2024-01-27 09:10',
          paymentMethod: 'Credit Card',
          cardLast4: '9012',
          description: 'Payment declined',
          refundable: false,
          failureReason: 'Insufficient funds'
        },
        {
          id: 'TXN-2024-006',
          merchant: 'Home Essentials',
          customer: 'Emily Davis',
          amount: 320.00,
          status: 'disputed',
          type: 'payment',
          date: '2024-01-26 15:30',
          paymentMethod: 'Credit Card',
          cardLast4: '3456',
          description: 'Service dispute - quality issue',
          refundable: true,
          disputeDate: '2024-01-28 10:00'
        }
      ];
      
      setTransactions(mockTransactions);
      setIsLoading(false);
    };

    loadTransactions();
  }, []);

  const handleRefund = async (transaction) => {
    if (!refundAmount || parseFloat(refundAmount) <= 0 || parseFloat(refundAmount) > transaction.amount) {
      addNotification({
        type: 'error',
        title: 'Invalid Amount',
        message: 'Please enter a valid refund amount',
        onClick: () => {},
        action: false
      });
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setTransactions(prev => prev.map(t => 
      t.id === transaction.id 
        ? { ...t, status: 'refunded', refundedAmount: parseFloat(refundAmount), refundDate: new Date().toISOString() }
        : t
    ));
    
    setSelectedTransaction(null);
    setRefundAmount('');
    setRefundReason('');
    
    addNotification({
      type: 'success',
      title: 'Refund Processed',
      message: `$${refundAmount} refunded to ${transaction.customer}`,
      onClick: () => {},
      action: false
    });
  };

  const handleResolveDispute = async (transaction, resolution) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setTransactions(prev => prev.map(t => 
      t.id === transaction.id 
        ? { ...t, status: resolution === 'approve' ? 'completed' : 'refunded' }
        : t
    ));
    
    setSelectedTransaction(null);
    
    addNotification({
      type: resolution === 'approve' ? 'success' : 'warning',
      title: 'Dispute Resolved',
      message: `Dispute ${resolution === 'approve' ? 'approved in favor of merchant' : 'refunded to customer'}`,
      onClick: () => {},
      action: false
    });
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter(txn => {
      const matchesSearch = 
        txn.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        txn.merchant.toLowerCase().includes(searchQuery.toLowerCase()) ||
        txn.customer.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesFilter = filterStatus === 'all' || txn.status === filterStatus;
      
      return matchesSearch && matchesFilter;
    });
  }, [transactions, searchQuery, filterStatus]);

  const stats = useMemo(() => ({
    total: transactions.reduce((sum, t) => sum + (t.status === 'completed' ? t.amount : 0), 0),
    completed: transactions.filter(t => t.status === 'completed').length,
    pending: transactions.filter(t => t.status === 'pending').length,
    refunded: transactions.filter(t => t.status === 'refunded').length,
    disputed: transactions.filter(t => t.status === 'disputed').length,
    failed: transactions.filter(t => t.status === 'failed').length
  }), [transactions]);

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-700 border-green-200',
      pending: 'bg-orange-100 text-orange-700 border-orange-200',
      refunded: 'bg-blue-100 text-blue-700 border-blue-200',
      disputed: 'bg-red-100 text-red-700 border-red-200',
      failed: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[status] || colors.completed;
  };

  const filterButtons = [
    { status: 'all', label: 'All', count: transactions.length },
    { status: 'completed', label: 'Completed', count: stats.completed },
    { status: 'pending', label: 'Pending', count: stats.pending },
    { status: 'disputed', label: 'Disputed', count: stats.disputed },
    { status: 'refunded', label: 'Refunded', count: stats.refunded }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">Transaction Management</h1>
          <p className="text-gray-300">Loading transactions...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, type: '', transaction: null })}
        onConfirm={() => {
          if (confirmDialog.type === 'refund') {
            setSelectedTransaction(confirmDialog.transaction);
          }
        }}
        title={confirmDialog.type === 'refund' ? 'Process Refund?' : 'Resolve Dispute?'}
        message={
          confirmDialog.type === 'refund'
            ? `Process refund for transaction ${confirmDialog.transaction?.id}?`
            : `Resolve dispute for transaction ${confirmDialog.transaction?.id}?`
        }
        confirmText={confirmDialog.type === 'refund' ? 'Proceed' : 'Resolve'}
        type="warning"
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Transaction Management Hub</h1>
            <p className="text-gray-300">Monitor and manage all platform transactions in real-time</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="24h" className="text-black">Last 24 Hours</option>
              <option value="7d" className="text-black">Last 7 Days</option>
              <option value="30d" className="text-black">Last 30 Days</option>
              <option value="90d" className="text-black">Last 90 Days</option>
            </select>
            <button className="px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-all flex items-center gap-2 font-semibold">
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards - FIXED: Matching Audit Logs Layout, NO PULSING */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" />
              +12.5%
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">${stats.total.toLocaleString()}</p>
          <p className="text-xs text-gray-500">This period</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
              <ArrowUpRight className="w-3 h-3" />
              +18%
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-1">Completed</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">{stats.completed}</p>
          <p className="text-xs text-gray-500">Successful transactions</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
            {/* REMOVED PULSING ANIMATION - Just show static badge if pending > 0 */}
            {stats.pending > 0 && (
              <span className="text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium">
                {stats.pending}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-1">Pending</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">{stats.pending}</p>
          <p className="text-xs text-gray-500">Awaiting confirmation</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            {/* REMOVED PULSING ANIMATION - Just show static badge if disputed > 0 */}
            {stats.disputed > 0 && (
              <span className="text-sm bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                {stats.disputed}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-1">Disputed</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">{stats.disputed}</p>
          <p className="text-xs text-red-600">Requires attention</p>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {filterButtons.map(({ status, label, count }) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
              filterStatus === status
                ? 'border-blue-500 bg-blue-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
            }`}
          >
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{count}</p>
              <p className="text-sm text-gray-600">{label}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by transaction ID, merchant, or customer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Transactions Table */}
      {filteredTransactions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <EmptyState
            icon={CreditCard}
            title="No Transactions Found"
            description="No transactions match your search criteria. Try adjusting your filters."
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Transaction ID</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Merchant</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTransactions.map(txn => (
                  <tr key={txn.id} className="hover:bg-blue-50 transition-colors group cursor-pointer" onClick={() => setSelectedTransaction(txn)}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span className="font-mono text-sm font-medium text-gray-900">{txn.id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{txn.merchant}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{txn.customer}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-lg font-bold text-gray-900">${txn.amount.toFixed(2)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(txn.status)}`}>
                        {txn.status.charAt(0).toUpperCase() + txn.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-600">{txn.date}</p>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        {txn.status === 'disputed' && (
                          <button
                            onClick={() => setSelectedTransaction(txn)}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                            title="Resolve Dispute"
                          >
                            <AlertTriangle className="w-4 h-4" />
                          </button>
                        )}
                        {txn.refundable && (
                          <button
                            onClick={() => setSelectedTransaction(txn)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title="Process Refund"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedTransaction(txn)}
                          className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                          title="View Details"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setSelectedTransaction(null); setRefundAmount(''); setRefundReason(''); }}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Transaction Details</h2>
                <p className="text-sm text-gray-500">{selectedTransaction.id}</p>
              </div>
              <button onClick={() => { setSelectedTransaction(null); setRefundAmount(''); setRefundReason(''); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Banner */}
              <div className={`rounded-xl p-4 border-2 ${getStatusColor(selectedTransaction.status)}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-lg">
                      {selectedTransaction.status.charAt(0).toUpperCase() + selectedTransaction.status.slice(1)}
                    </p>
                    <p className="text-sm opacity-75">{selectedTransaction.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold">${selectedTransaction.amount.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Transaction Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Merchant</p>
                  <p className="font-semibold text-gray-900">{selectedTransaction.merchant}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Customer</p>
                  <p className="font-semibold text-gray-900">{selectedTransaction.customer}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                  <p className="font-semibold text-gray-900">
                    {selectedTransaction.paymentMethod}
                    {selectedTransaction.cardLast4 && ` •••• ${selectedTransaction.cardLast4}`}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs text-gray-500 mb-1">Date & Time</p>
                  <p className="font-semibold text-gray-900">{selectedTransaction.date}</p>
                </div>
              </div>

              {/* Refund Info */}
              {selectedTransaction.status === 'refunded' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Refund Information</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-blue-700">Refunded Amount</p>
                      <p className="font-semibold text-blue-900">${selectedTransaction.refundedAmount?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700">Refund Date</p>
                      <p className="font-semibold text-blue-900">{selectedTransaction.refundDate}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Dispute Info */}
              {selectedTransaction.status === 'disputed' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Dispute Information
                  </h4>
                  <p className="text-sm text-red-700 mb-4">
                    This transaction is under dispute. Customer reported: "{selectedTransaction.description}"
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleResolveDispute(selectedTransaction, 'approve')}
                      className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
                    >
                      Approve Merchant
                    </button>
                    <button
                      onClick={() => handleResolveDispute(selectedTransaction, 'refund')}
                      className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold"
                    >
                      Refund Customer
                    </button>
                  </div>
                </div>
              )}

              {/* Refund Form */}
              {selectedTransaction.refundable && selectedTransaction.status !== 'refunded' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-semibold text-orange-900 mb-4">Process Refund</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Refund Amount (Max: ${selectedTransaction.amount.toFixed(2)})
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        max={selectedTransaction.amount}
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Refund</label>
                      <textarea
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        placeholder="Explain why this refund is being processed..."
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                    <button
                      onClick={() => handleRefund(selectedTransaction)}
                      disabled={!refundAmount || !refundReason}
                      className="w-full py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Process Refund
                    </button>
                  </div>
                </div>
              )}

              {/* Failure Info */}
              {selectedTransaction.status === 'failed' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Failure Reason</h4>
                  <p className="text-sm text-gray-700">{selectedTransaction.failureReason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// SUPPORT TICKETING SYSTEM COMPONENT
const SupportTicketing = ({ addNotification }) => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [replyText, setReplyText] = useState('');

  // Mock ticket data
  useEffect(() => {
    const loadTickets = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockTickets = [
        {
          id: 'TKT-001',
          merchant: 'Tech Haven Store',
          merchantId: 'MERCH-123',
          subject: 'Unable to upload business documents',
          message: 'I\'m trying to upload my business license but keep getting an error "File upload failed". I\'ve tried multiple times with different file formats (PDF, JPG) but nothing works. This is preventing me from completing my merchant verification. Please help urgently as I need to start accepting payments.',
          status: 'open',
          priority: 'urgent',
          created: '2024-01-28 14:30',
          lastUpdate: '2024-01-28 14:30',
          assignedTo: 'Admin Sarah',
          replies: []
        },
        {
          id: 'TKT-002',
          merchant: 'Gourmet Kitchen',
          merchantId: 'MERCH-456',
          subject: 'Payment processing delay',
          message: 'Our customers are reporting that their payments are taking longer than usual to process. Usually it\'s instant but now it\'s taking 5-10 minutes. Is there a system issue?',
          status: 'in_progress',
          priority: 'high',
          created: '2024-01-28 13:15',
          lastUpdate: '2024-01-28 14:00',
          assignedTo: 'Admin John',
          replies: [
            {
              from: 'Admin John',
              message: 'We\'re looking into this issue. Our technical team is investigating the delay.',
              timestamp: '2024-01-28 13:45'
            }
          ]
        },
        {
          id: 'TKT-003',
          merchant: 'Urban Style Store',
          merchantId: 'MERCH-789',
          subject: 'How to update business hours',
          message: 'I need to change our store hours in the system. Where can I do this?',
          status: 'in_progress',
          priority: 'low',
          created: '2024-01-28 11:20',
          lastUpdate: '2024-01-28 12:00',
          assignedTo: 'Admin Sarah',
          replies: [
            {
              from: 'Admin Sarah',
              message: 'You can update your business hours in Settings > Store Information > Business Hours.',
              timestamp: '2024-01-28 11:45'
            }
          ]
        },
        {
          id: 'TKT-004',
          merchant: 'Digital Solutions',
          merchantId: 'MERCH-321',
          subject: 'API integration documentation',
          message: 'I need detailed documentation for integrating your payment API with our e-commerce platform. Specifically looking for Node.js examples.',
          status: 'resolved',
          priority: 'medium',
          created: '2024-01-27 16:30',
          lastUpdate: '2024-01-27 18:00',
          assignedTo: 'Admin Mike',
          resolvedAt: '2024-01-27 18:00',
          replies: [
            {
              from: 'Admin Mike',
              message: 'Please check our developer documentation at https://docs.helpsum.com/api. We have comprehensive Node.js integration examples there.',
              timestamp: '2024-01-27 17:15'
            },
            {
              from: 'Digital Solutions',
              message: 'Perfect! Found everything I needed. Thanks!',
              timestamp: '2024-01-27 17:45'
            }
          ]
        },
        {
          id: 'TKT-005',
          merchant: 'Fashion Boutique',
          merchantId: 'MERCH-654',
          subject: 'Refund request - Transaction TXN-2024-089',
          message: 'Customer is requesting a refund for transaction TXN-2024-089. Amount: $250. Product was damaged during shipping.',
          status: 'open',
          priority: 'urgent',
          created: '2024-01-28 10:00',
          lastUpdate: '2024-01-28 10:00',
          assignedTo: null,
          replies: []
        },
        {
          id: 'TKT-006',
          merchant: 'Green Market',
          merchantId: 'MERCH-987',
          subject: 'Account verification status',
          message: 'It\'s been 3 days since I submitted my verification documents. When will my account be approved?',
          status: 'open',
          priority: 'medium',
          created: '2024-01-28 09:30',
          lastUpdate: '2024-01-28 09:30',
          assignedTo: null,
          replies: []
        }
      ];
      
      setTickets(mockTickets);
      setIsLoading(false);
    };

    loadTickets();
  }, []);

  const handleStatusChange = async (ticketId, newStatus) => {
    setActionLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { 
          ...t, 
          status: newStatus,
          resolvedAt: newStatus === 'resolved' ? new Date().toISOString() : null
        } : t
      ));
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ 
          ...prev, 
          status: newStatus,
          resolvedAt: newStatus === 'resolved' ? new Date().toISOString() : null
        }));
      }
      addNotification({
        type: 'success',
        title: 'Status Updated',
        message: `Ticket ${ticketId} marked as ${newStatus}`,
        onClick: () => {},
        action: false
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to update ticket status',
        onClick: () => {},
        action: false
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    
    setActionLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const newReply = {
        from: 'Support Admin',
        message: replyText,
        timestamp: new Date().toLocaleString()
      };
      
      setTickets(prev => prev.map(t => 
        t.id === selectedTicket.id 
          ? { ...t, replies: [...t.replies, newReply], lastUpdate: newReply.timestamp }
          : t
      ));
      
      setSelectedTicket(prev => ({
        ...prev,
        replies: [...prev.replies, newReply],
        lastUpdate: newReply.timestamp
      }));
      
      setReplyText('');
      addNotification({
        type: 'success',
        title: 'Reply Sent',
        message: 'Your response has been sent to the merchant',
        onClick: () => {},
        action: false
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to send reply',
        onClick: () => {},
        action: false
      });
    } finally {
      setActionLoading(false);
    }
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      const matchesSearch = 
        ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.merchant.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
      const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
      
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tickets, searchQuery, filterStatus, filterPriority]);

  const stats = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    urgent: tickets.filter(t => t.priority === 'urgent').length
  }), [tickets]);

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-700 border-red-200',
      'high': 'bg-orange-100 text-orange-700 border-orange-200',
      'medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'low': 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': 'bg-orange-100 text-orange-700 border-orange-200',
      'in_progress': 'bg-blue-100 text-blue-700 border-blue-200',
      'resolved': 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[status] || colors.open;
  };

  const exportReport = () => {
    addNotification({
      type: 'success',
      title: 'Report Exported',
      message: 'Support ticket report exported successfully',
      onClick: () => {},
      action: false
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">Support Ticketing System</h1>
          <p className="text-gray-300">Loading tickets...</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Support Ticketing System</h1>
            <p className="text-gray-300">Manage merchant support requests efficiently</p>
          </div>
          <button onClick={exportReport} className="px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-all flex items-center gap-2 font-semibold">
            <Download className="w-4 h-4" />
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Cards - Using only Transaction component icons */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Total Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Open Tickets</p>
              <p className="text-2xl font-bold text-gray-900">{stats.open}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inProgress}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Urgent</p>
              <p className="text-2xl font-bold text-gray-900">{stats.urgent}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets by ID, merchant, or subject..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <EmptyState
            icon={CreditCard}
            title="No Tickets Found"
            description="No support tickets match your search criteria."
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Support Tickets</h3>
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-mono text-sm text-gray-500">{ticket.id}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ').charAt(0).toUpperCase() + ticket.status.replace('_', ' ').slice(1)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                        </span>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{ticket.subject}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {ticket.merchant}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {ticket.created}
                        </span>
                        {ticket.assignedTo && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {ticket.assignedTo}
                          </span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                  </div>
                  
                  <p className="text-sm text-gray-700 line-clamp-2">{ticket.message}</p>
                  
                  {ticket.replies.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">
                        {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedTicket(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="font-mono text-sm text-gray-500">{selectedTicket.id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(selectedTicket.status)}`}>
                    {selectedTicket.status.replace('_', ' ').charAt(0).toUpperCase() + selectedTicket.status.replace('_', ' ').slice(1)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(selectedTicket.priority)}`}>
                    {selectedTicket.priority.charAt(0).toUpperCase() + selectedTicket.priority.slice(1)}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedTicket.subject}</h2>
                <p className="text-sm text-gray-500">{selectedTicket.merchant} • {selectedTicket.created}</p>
              </div>
              <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Original Message */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Original Message</h4>
                <p className="text-gray-700">{selectedTicket.message}</p>
              </div>

              {/* Replies */}
              {selectedTicket.replies.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Conversation</h4>
                  {selectedTicket.replies.map((reply, index) => (
                    <div key={index} className={`p-4 rounded-lg ${
                      reply.from.startsWith('Admin') 
                        ? 'bg-blue-50 border border-blue-200 ml-8' 
                        : 'bg-gray-50 border border-gray-200 mr-8'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm">{reply.from}</span>
                        <span className="text-xs text-gray-500">{reply.timestamp}</span>
                      </div>
                      <p className="text-gray-700">{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Box */}
              {selectedTicket.status !== 'resolved' && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Send Reply</h4>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your response to the merchant..."
                    className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[120px]"
                  />
                  <div className="flex gap-3">
                    <button
                      onClick={handleReply}
                      disabled={actionLoading || !replyText.trim()}
                      className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading ? <Clock className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                      Send Reply
                    </button>
                  </div>
                </div>
              )}

              {/* Status Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                {selectedTicket.status === 'open' && (
                  <button
                    onClick={() => handleStatusChange(selectedTicket.id, 'in_progress')}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-semibold transition-all disabled:opacity-50"
                  >
                    Mark In Progress
                  </button>
                )}
                {selectedTicket.status !== 'resolved' && (
                  <button
                    onClick={() => handleStatusChange(selectedTicket.id, 'resolved')}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-all disabled:opacity-50"
                  >
                    Mark Resolved
                  </button>
                )}
                {selectedTicket.status === 'resolved' && (
                  <button
                    onClick={() => handleStatusChange(selectedTicket.id, 'open')}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold transition-all disabled:opacity-50"
                  >
                    Reopen Ticket
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// AUDIT LOGS & ACTIVITY TRACKING COMPONENT
const AuditLogs = ({ addNotification }) => {
  const [logs, setLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);

  // Mock audit log data - replace with API call
  useEffect(() => {
    const loadLogs = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockLogs = [
        {
          id: 'LOG-001',
          action: 'merchant_approved',
          actionLabel: 'Merchant Approved',
          user: 'Admin Sarah',
          userId: 'ADM-001',
          userEmail: 'sarah@helpsum.com',
          ipAddress: '192.168.1.100',
          timestamp: '2024-01-28 15:45:32',
          details: {
            merchantId: 'MERCH-123',
            merchantName: 'Tech Haven Store',
            previousStatus: 'pending',
            newStatus: 'approved'
          },
          category: 'merchant_management',
          severity: 'info'
        },
        {
          id: 'LOG-002',
          action: 'transaction_refunded',
          actionLabel: 'Transaction Refunded',
          user: 'Admin John',
          userId: 'ADM-002',
          userEmail: 'john@helpsum.com',
          ipAddress: '192.168.1.105',
          timestamp: '2024-01-28 14:30:15',
          details: {
            transactionId: 'TXN-2024-003',
            amount: 150.00,
            reason: 'Customer request - service cancelled',
            merchantName: 'Gourmet Kitchen'
          },
          category: 'financial',
          severity: 'warning'
        },
        {
          id: 'LOG-003',
          action: 'settings_changed',
          actionLabel: 'Settings Changed',
          user: 'Admin Sarah',
          userId: 'ADM-001',
          userEmail: 'sarah@helpsum.com',
          ipAddress: '192.168.1.100',
          timestamp: '2024-01-28 13:20:45',
          details: {
            settingName: 'notification_preferences',
            previousValue: 'email_only',
            newValue: 'email_and_push',
            section: 'System Settings'
          },
          category: 'system_config',
          severity: 'info'
        },
        {
          id: 'LOG-004',
          action: 'merchant_deleted',
          actionLabel: 'Merchant Deleted',
          user: 'Admin Mike',
          userId: 'ADM-003',
          userEmail: 'mike@helpsum.com',
          ipAddress: '192.168.1.110',
          timestamp: '2024-01-28 12:15:20',
          details: {
            merchantId: 'MERCH-456',
            merchantName: 'Urban Style Store',
            reason: 'Fraudulent activity detected',
            deletedData: 'All merchant data permanently removed'
          },
          category: 'merchant_management',
          severity: 'critical'
        },
        {
          id: 'LOG-005',
          action: 'login_success',
          actionLabel: 'Login Success',
          user: 'Admin Sarah',
          userId: 'ADM-001',
          userEmail: 'sarah@helpsum.com',
          ipAddress: '192.168.1.100',
          timestamp: '2024-01-28 09:00:12',
          details: {
            loginMethod: 'Email & Password',
            deviceType: 'Desktop',
            browser: 'Chrome 120.0',
            location: 'New York, US'
          },
          category: 'authentication',
          severity: 'info'
        },
        {
          id: 'LOG-006',
          action: 'login_failed',
          actionLabel: 'Login Failed',
          user: 'Unknown',
          userId: null,
          userEmail: 'suspicious@email.com',
          ipAddress: '45.123.456.789',
          timestamp: '2024-01-28 08:45:30',
          details: {
            loginMethod: 'Email & Password',
            failureReason: 'Invalid credentials',
            attemptCount: 3,
            location: 'Unknown'
          },
          category: 'authentication',
          severity: 'warning'
        },
        {
          id: 'LOG-007',
          action: 'merchant_rejected',
          actionLabel: 'Merchant Rejected',
          user: 'Admin John',
          userId: 'ADM-002',
          userEmail: 'john@helpsum.com',
          ipAddress: '192.168.1.105',
          timestamp: '2024-01-27 16:30:45',
          details: {
            merchantId: 'MERCH-789',
            merchantName: 'Quick Service Co',
            previousStatus: 'pending',
            newStatus: 'rejected',
            reason: 'Incomplete documentation'
          },
          category: 'merchant_management',
          severity: 'info'
        },
        {
          id: 'LOG-008',
          action: 'api_key_generated',
          actionLabel: 'API Key Generated',
          user: 'Admin Sarah',
          userId: 'ADM-001',
          userEmail: 'sarah@helpsum.com',
          ipAddress: '192.168.1.100',
          timestamp: '2024-01-27 15:10:20',
          details: {
            keyType: 'Production',
            keyPrefix: 'hps_live_sk_...',
            expiresAt: '2025-01-27',
            permissions: 'Full Access'
          },
          category: 'security',
          severity: 'warning'
        },
        {
          id: 'LOG-009',
          action: 'ticket_resolved',
          actionLabel: 'Support Ticket Resolved',
          user: 'Admin John',
          userId: 'ADM-002',
          userEmail: 'john@helpsum.com',
          ipAddress: '192.168.1.105',
          timestamp: '2024-01-27 14:00:10',
          details: {
            ticketId: 'TKT-004',
            merchantName: 'Digital Solutions',
            issue: 'How to update business hours',
            resolutionTime: '1.5 hours'
          },
          category: 'support',
          severity: 'info'
        },
        {
          id: 'LOG-010',
          action: 'bulk_merchant_approval',
          actionLabel: 'Bulk Merchant Approval',
          user: 'Admin Mike',
          userId: 'ADM-003',
          userEmail: 'mike@helpsum.com',
          ipAddress: '192.168.1.110',
          timestamp: '2024-01-27 11:30:50',
          details: {
            merchantCount: 5,
            merchantIds: ['MERCH-101', 'MERCH-102', 'MERCH-103', 'MERCH-104', 'MERCH-105'],
            batchId: 'BATCH-2024-001'
          },
          category: 'merchant_management',
          severity: 'info'
        },
        {
          id: 'LOG-011',
          action: 'password_changed',
          actionLabel: 'Password Changed',
          user: 'Admin Sarah',
          userId: 'ADM-001',
          userEmail: 'sarah@helpsum.com',
          ipAddress: '192.168.1.100',
          timestamp: '2024-01-26 10:20:30',
          details: {
            changeMethod: 'Manual',
            securityVerified: true,
            previousPasswordAge: '45 days'
          },
          category: 'security',
          severity: 'info'
        },
        {
          id: 'LOG-012',
          action: 'data_exported',
          actionLabel: 'Data Exported',
          user: 'Admin John',
          userId: 'ADM-002',
          userEmail: 'john@helpsum.com',
          ipAddress: '192.168.1.105',
          timestamp: '2024-01-26 09:15:20',
          details: {
            exportType: 'Merchant Data',
            format: 'CSV',
            recordCount: 1284,
            fileSize: '2.5 MB'
          },
          category: 'data_management',
          severity: 'warning'
        }
      ];
      
      setLogs(mockLogs);
      setIsLoading(false);
    };

    loadLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const matchesSearch = 
        log.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.actionLabel.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.user.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.ipAddress.includes(searchQuery);
      
      const matchesAction = filterAction === 'all' || log.category === filterAction;
      const matchesUser = filterUser === 'all' || log.userId === filterUser;
      
      return matchesSearch && matchesAction && matchesUser;
    });
  }, [logs, searchQuery, filterAction, filterUser]);

  const stats = useMemo(() => ({
    total: logs.length,
    today: logs.filter(l => l.timestamp.startsWith('2024-01-28')).length,
    critical: logs.filter(l => l.severity === 'critical').length,
    warnings: logs.filter(l => l.severity === 'warning').length,
    uniqueUsers: [...new Set(logs.map(l => l.userId).filter(Boolean))].length
  }), [logs]);

  const getSeverityColor = (severity) => {
    const colors = {
      'info': 'bg-blue-100 text-blue-700 border-blue-200',
      'warning': 'bg-orange-100 text-orange-700 border-orange-200',
      'critical': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[severity] || colors.info;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'merchant_management': User,
      'financial': DollarSign,
      'system_config': CreditCard,
      'authentication': User,
      'security': AlertTriangle,
      'support': User,
      'data_management': CreditCard
    };
    return icons[category] || CheckCircle;
  };

  const exportLogs = () => {
    addNotification({
      type: 'success',
      title: 'Logs Exported',
      message: 'Audit logs exported to CSV successfully',
      onClick: () => {},
      action: false
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">Audit Logs</h1>
          <p className="text-gray-300">Loading activity logs...</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Audit Logs & Activity Tracking</h1>
            <p className="text-gray-300">Complete accountability and security trail</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="24h" className="text-black">Last 24 Hours</option>
              <option value="7d" className="text-black">Last 7 Days</option>
              <option value="30d" className="text-black">Last 30 Days</option>
              <option value="90d" className="text-black">Last 90 Days</option>
            </select>
            <button onClick={exportLogs} className="px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-all flex items-center gap-2 font-semibold">
              <Download className="w-4 h-4" />
              Export Logs
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Using only Transaction component icons */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Total Logs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Today</p>
              <p className="text-2xl font-bold text-gray-900">{stats.today}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Critical</p>
              <p className="text-2xl font-bold text-gray-900">{stats.critical}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Warnings</p>
              <p className="text-2xl font-bold text-gray-900">{stats.warnings}</p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-xl border-2 border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">Active Admins</p>
              <p className="text-2xl font-bold text-gray-900">{stats.uniqueUsers}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs by ID, action, user, or IP..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Categories</option>
            <option value="merchant_management">Merchant Management</option>
            <option value="financial">Financial</option>
            <option value="authentication">Authentication</option>
            <option value="security">Security</option>
            <option value="support">Support</option>
            <option value="system_config">System Config</option>
            <option value="data_management">Data Management</option>
          </select>

          <select
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Users</option>
            <option value="ADM-001">Admin Sarah</option>
            <option value="ADM-002">Admin John</option>
            <option value="ADM-003">Admin Mike</option>
          </select>
        </div>
      </div>

      {/* Logs Timeline */}
      {filteredLogs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <EmptyState
            icon={CreditCard}
            title="No Logs Found"
            description="No audit logs match your search criteria."
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
          <div className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Activity Timeline</h3>
            <div className="space-y-4">
              {filteredLogs.map((log) => {
                const CategoryIcon = getCategoryIcon(log.category);
                return (
                  <div
                    key={log.id}
                    className="relative pl-8 pb-4 border-l-2 border-gray-200 hover:border-blue-400 transition-colors cursor-pointer group"
                    onClick={() => setSelectedLog(log)}
                  >
                    {/* Timeline dot - REMOVED PULSING ANIMATION */}
                    <div className={`absolute left-[-9px] top-0 w-4 h-4 rounded-full border-2 border-white ${
                      log.severity === 'critical' ? 'bg-red-500' :
                      log.severity === 'warning' ? 'bg-orange-500' :
                      'bg-blue-500'
                    }`} />

                    {/* Log Content */}
                    <div className="bg-gray-50 rounded-lg p-4 group-hover:bg-blue-50 transition-colors">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            log.severity === 'critical' ? 'bg-red-100' :
                            log.severity === 'warning' ? 'bg-orange-100' :
                            'bg-blue-100'
                          }`}>
                            <CategoryIcon className={`w-5 h-5 ${
                              log.severity === 'critical' ? 'text-red-600' :
                              log.severity === 'warning' ? 'text-orange-600' :
                              'text-blue-600'
                            }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-gray-900">{log.actionLabel}</h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(log.severity)}`}>
                                {log.severity.charAt(0).toUpperCase() + log.severity.slice(1)}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {log.user}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {log.timestamp}
                              </span>
                              <span className="flex items-center gap-1 font-mono">
                                {log.ipAddress}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
                      </div>

                      {/* Quick details preview */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          {Object.entries(log.details).slice(0, 2).map(([key, value]) => (
                            <div key={key} className="truncate">
                              <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="ml-1 font-medium text-gray-900">
                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-sm text-gray-500">{selectedLog.id}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(selectedLog.severity)}`}>
                    {selectedLog.severity.charAt(0).toUpperCase() + selectedLog.severity.slice(1)}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedLog.actionLabel}</h2>
                <p className="text-sm text-gray-500">{selectedLog.timestamp}</p>
              </div>
              <button onClick={() => setSelectedLog(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3">User Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-blue-700">Admin User</p>
                    <p className="font-semibold text-blue-900">{selectedLog.user}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700">User ID</p>
                    <p className="font-semibold text-blue-900 font-mono">{selectedLog.userId || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700">Email</p>
                    <p className="font-semibold text-blue-900">{selectedLog.userEmail}</p>
                  </div>
                  <div>
                    <p className="text-xs text-blue-700">IP Address</p>
                    <p className="font-semibold text-blue-900 font-mono">{selectedLog.ipAddress}</p>
                  </div>
                </div>
              </div>

              {/* Action Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Action Details</h4>
                <div className="space-y-3">
                  {Object.entries(selectedLog.details).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-3 pb-3 border-b border-gray-200 last:border-0">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">
                          {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </p>
                        <p className="font-medium text-gray-900">
                          {typeof value === 'object' 
                            ? JSON.stringify(value, null, 2)
                            : typeof value === 'number'
                            ? value.toLocaleString()
                            : String(value)
                          }
                        </p>
                      </div>
                      {typeof value === 'string' && (value.startsWith('MERCH-') || value.startsWith('TXN-') || value.startsWith('TKT-')) && (
                        <button className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors">
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Metadata</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Category</p>
                    <p className="font-medium text-gray-900">
                      {selectedLog.category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Action Type</p>
                    <p className="font-medium text-gray-900">{selectedLog.action}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// Dashboard Component - Part 1: Setup and Data
const Dashboard = ({ addNotification }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const result = await api.getDashboard();
        if (result.success) {
          setDashboardData(result.data);
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load dashboard data',
          onClick: () => {},
          action: false
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
  }, [addNotification]);

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const result = await api.getDashboard();
      if (result.success) {
        setDashboardData(result.data);
        addNotification({
          type: 'success',
          title: 'Refreshed',
          message: 'Dashboard data updated successfully',
          onClick: () => {},
          action: false
        });
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to refresh dashboard',
        onClick: () => {},
        action: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const stats = useMemo(() => {
    if (!dashboardData) {
      return [
        { icon: Users, label: 'Total Merchants', value: '0', change: '+0%', subtext: '0 this month', color: 'blue' },
        { icon: DollarSign, label: 'Total Revenue', value: '$0', change: '+0%', subtext: '$0 this week', color: 'green' },
        { icon: ShoppingCart, label: 'Total Orders', value: '0', change: '+0%', subtext: '0 today', color: 'purple' },
        { icon: Clock, label: 'Pending Approvals', value: '0', change: '0', subtext: 'Requires attention', color: 'orange', pulse: false }
      ];
    }

    return [
      { 
        icon: Users, 
        label: 'Total Merchants', 
        value: dashboardData.totalMerchants?.toLocaleString() || '0', 
        change: '+12.5%', 
        subtext: `+${dashboardData.recentUsers || 0} this month`, 
        color: 'blue' 
      },
      { 
        icon: DollarSign, 
        label: 'Total Revenue', 
        value: `$${(dashboardData.totalRevenue || 0).toLocaleString()}`, 
        change: '+18.2%', 
        subtext: 'This period', 
        color: 'green' 
      },
      { 
        icon: ShoppingCart, 
        label: 'Total Orders', 
        value: (dashboardData.totalOrders || 0).toLocaleString(), 
        change: '+23.1%', 
        subtext: `${dashboardData.completedJobs || 0} completed`, 
        color: 'purple' 
      },
      { 
        icon: Clock, 
        label: 'Pending Approvals', 
        value: (dashboardData.pendingApprovals || 0).toString(), 
        change: (dashboardData.pendingApprovals || 0).toString(), 
        subtext: 'Requires attention', 
        color: 'orange', 
        pulse: (dashboardData.pendingApprovals || 0) > 0 
      }
    ];
  }, [dashboardData]);

  const transactionData = [
    { day: 'Mon', amount: 4500 }, { day: 'Tue', amount: 5200 }, { day: 'Wed', amount: 4800 },
    { day: 'Thu', amount: 6100 }, { day: 'Fri', amount: 7200 }, { day: 'Sat', amount: 6800 }, { day: 'Sun', amount: 5500 }
  ];

  const growthData = [
    { month: 'Jan', merchants: 856 }, { month: 'Feb', merchants: 923 }, { month: 'Mar', merchants: 1045 },
    { month: 'Apr', merchants: 1123 }, { month: 'May', merchants: 1198 },
    { month: 'Jun', merchants: dashboardData?.totalMerchants || 1284 }
  ];

  const merchantDistribution = [
    { category: 'Electronics', count: 342, color: 'bg-blue-500', percentage: 27 },
    { category: 'Fashion', count: 289, color: 'bg-purple-500', percentage: 22 },
    { category: 'Food & Beverage', count: 256, color: 'bg-green-500', percentage: 20 },
    { category: 'Services', count: 218, color: 'bg-orange-500', percentage: 17 },
    { category: 'Other', count: 179, color: 'bg-gray-500', percentage: 14 }
  ];

  const activities = [
    { merchant: 'Tech Haven Store', action: 'New application submitted', time: '2 min ago', type: 'new' },
    { merchant: 'Fashion Boutique', action: 'Approved and activated', time: '15 min ago', type: 'approved' },
    { merchant: 'Gourmet Kitchen', action: 'Documents uploaded', time: '32 min ago', type: 'update' },
    { merchant: 'Digital Solutions', action: 'Payment processed - $2,450', time: '1 hour ago', type: 'payment' },
    { merchant: 'Urban Style', action: 'Profile updated', time: '2 hours ago', type: 'update' },
    { merchant: 'Home Essentials', action: 'New application submitted', time: '3 hours ago', type: 'new' },
    { merchant: 'Elite Boutique', action: 'Verification completed', time: '4 hours ago', type: 'approved' },
    { merchant: 'Fresh Market', action: 'Inventory updated', time: '5 hours ago', type: 'update' },
    { merchant: 'Tech Pro Services', action: 'Monthly payout - $8,340', time: '6 hours ago', type: 'payment' }
  ];

  const quickActions = useMemo(() => [
    { label: `${dashboardData?.pendingApprovals || 0} Pending Approvals`, icon: Clock, color: 'orange', action: 'Review Now' },
    { label: `${dashboardData?.todayUsers || 0} New Applications`, icon: Users, color: 'blue', action: 'View All' },
    { label: '8 Documents to Review', icon: FileText, color: 'purple', action: 'Check Now' }
  ], [dashboardData]);

  const displayedActivities = showAllActivity ? activities : activities.slice(0, 3);
  const maxTransaction = Math.max(...transactionData.map(d => d.amount));
  const maxGrowth = Math.max(...growthData.map(d => d.merchants));

  const getActivityIcon = (type) => {
    const icons = {
      new: { Icon: Users, color: 'bg-blue-100 text-blue-600' },
      approved: { Icon: CheckCircle, color: 'bg-green-100 text-green-600' },
      payment: { Icon: DollarSign, color: 'bg-purple-100 text-purple-600' },
      update: { Icon: Activity, color: 'bg-gray-100 text-gray-600' }
    };
    return icons[type] || icons.update;
  };

  const exportToPDF = () => {
    addNotification({ type: 'success', title: 'Report Generated', message: 'Dashboard report has been exported to PDF successfully.', onClick: () => {}, action: false });
  };

  const exportToCSV = () => {
    addNotification({ type: 'success', title: 'Data Exported', message: 'Merchant data has been exported to CSV successfully.', onClick: () => {}, action: false });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">Dashboard Overview</h1>
          <p className="text-gray-300">Loading your analytics...</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Dashboard Overview</h1>
            <p className="text-gray-300">Welcome back, Admin • Real-time analytics</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={handleRefresh} className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm hover:bg-white/20 transition-all flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button onClick={exportToCSV} className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm hover:bg-white/20 transition-all flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export CSV
            </button>
            <button onClick={exportToPDF} className="px-4 py-2 bg-yellow-400 text-black rounded-lg text-sm hover:bg-yellow-500 transition-all flex items-center gap-2 font-semibold">
              <Download className="w-4 h-4" />
              PDF Report
            </button>
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400">
              <option value="24h" className="text-black">Last 24 Hours</option>
              <option value="7d" className="text-black">Last 7 Days</option>
              <option value="30d" className="text-black">Last 30 Days</option>
              <option value="90d" className="text-black">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <div key={i} className={`bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all transform hover:-translate-y-1 cursor-pointer ${stat.pulse ? 'relative' : ''}`}>
            {stat.pulse && (
              <div className="absolute top-4 right-4">
                <span className="flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                </span>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color === 'blue' ? 'bg-blue-50' : stat.color === 'green' ? 'bg-green-50' : stat.color === 'purple' ? 'bg-purple-50' : 'bg-orange-50'} rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color === 'blue' ? 'text-blue-600' : stat.color === 'green' ? 'text-green-600' : stat.color === 'purple' ? 'text-purple-600' : 'text-orange-600'}`} />
              </div>
              <span className={`text-sm ${stat.color === 'orange' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'} px-2 py-1 rounded-full font-medium flex items-center gap-1`}>
                {typeof stat.change === 'string' && stat.change.includes('+') && <ArrowUpRight className="w-3 h-3" />}
                {stat.change}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
            <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.subtext}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border-2 border-yellow-200 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Quick Actions</h3>
            <p className="text-sm text-gray-600">Items requiring your attention</p>
          </div>
          <Zap className="w-6 h-6 text-yellow-600" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickActions.map((action, i) => (
            <div key={i} className="bg-white rounded-lg p-4 border border-gray-200 hover:border-yellow-400 hover:shadow-md transition-all cursor-pointer group">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 ${action.color === 'orange' ? 'bg-orange-100' : action.color === 'blue' ? 'bg-blue-100' : 'bg-purple-100'} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                  <action.icon className={`w-5 h-5 ${action.color === 'orange' ? 'text-orange-600' : action.color === 'blue' ? 'text-blue-600' : 'text-purple-600'}`} />
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-yellow-600 transition-colors" />
              </div>
              <p className="font-semibold text-gray-900 mb-1">{action.label}</p>
              <p className="text-sm text-yellow-600 font-medium">{action.action} →</p>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Activity */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Transaction Activity</h3>
              <p className="text-sm text-gray-500">Daily transaction volume</p>
            </div>
            <Activity className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {transactionData.map((item, i) => (
              <div key={i} className="flex items-center gap-4 group">
                <span className="text-sm font-medium text-gray-600 w-12">{item.day}</span>
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-10 overflow-hidden relative">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-end pr-3 transition-all duration-500 group-hover:from-blue-600 group-hover:to-blue-700"
                      style={{ width: `${(item.amount / maxTransaction) * 100}%` }}
                    >
                      <span className="text-xs font-semibold text-white">${(item.amount / 1000).toFixed(1)}k</span>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-20 text-right">{item.amount.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Merchant Growth */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Merchant Growth</h3>
              <p className="text-sm text-gray-500">Monthly new merchants</p>
            </div>
            <BarChart2 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {growthData.map((item, i) => (
              <div key={i} className="flex items-center gap-4 group">
                <span className="text-sm font-medium text-gray-600 w-12">{item.month}</span>
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 bg-gray-100 rounded-full h-10 overflow-hidden relative">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-end pr-3 transition-all duration-500 group-hover:from-green-600 group-hover:to-green-700"
                      style={{ width: `${(item.merchants / maxGrowth) * 100}%` }}
                    >
                      <span className="text-xs font-semibold text-white">{item.merchants}</span>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-900 w-20 text-right">{item.merchants}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Categories</h3>
            <p className="text-sm text-gray-500">By merchant type</p>
          </div>
        </div>
        <div className="space-y-4">
          {merchantDistribution.map((item, i) => (
            <div key={i}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${item.color}`}></div>
                  <span className="text-sm font-medium text-gray-700">{item.category}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-gray-900">{item.count}</span>
                  <span className="text-xs text-gray-500 ml-2">({item.percentage}%)</span>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className={`h-full ${item.color} rounded-full transition-all duration-500`} style={{ width: `${item.percentage}%` }}></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-lg transition-shadow">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Recent Activity</h3>
            <p className="text-sm text-gray-500">Latest merchant activities</p>
          </div>
          <button onClick={() => setShowAllActivity(!showAllActivity)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors">
            {showAllActivity ? 'Show Less' : 'View All'}
            {showAllActivity ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
        <div className="space-y-3">
          {displayedActivities.map((activity, i) => {
            const { Icon, color } = getActivityIcon(activity.type);
            return (
              <div key={i} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-all cursor-pointer group">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{activity.merchant}</p>
                  <p className="text-sm text-gray-600">{activity.action}</p>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">{activity.time}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Merchant Verification Component
const MerchantVerification = ({ addNotification }) => {
  const [merchants, setMerchants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', merchant: null });
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const result = await api.getMerchants();
        if (result.success) {
          setMerchants(result.data);
        }
      } catch (error) {
        addNotification({ type: 'error', title: 'Error', message: 'Failed to load merchants', onClick: () => {}, action: false });
      } finally {
        setIsLoading(false);
      }
    };
    fetchMerchants();
  }, [addNotification]);

  const handleApprove = useCallback(async (merchant) => {
    setActionLoading(true);
    try {
      await api.approveMerchant(merchant.id);
      setMerchants(prev => prev.map(m => m.id === merchant.id ? { ...m, status: 'approved' } : m));
      setSelectedMerchant(null);
      addNotification({ type: 'success', title: 'Merchant Approved', message: `${merchant.name} has been successfully approved.`, onClick: () => {}, action: true });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: error.message || 'Failed to approve merchant', onClick: () => {}, action: false });
    } finally {
      setActionLoading(false);
    }
  }, [addNotification]);

  const handleReject = useCallback(async (merchant) => {
    setActionLoading(true);
    try {
      await api.rejectMerchant(merchant.id);
      setMerchants(prev => prev.map(m => m.id === merchant.id ? { ...m, status: 'rejected' } : m));
      setSelectedMerchant(null);
      addNotification({ type: 'warning', title: 'Merchant Rejected', message: `${merchant.name} application has been rejected.`, onClick: () => {}, action: true });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: error.message || 'Failed to reject merchant', onClick: () => {}, action: false });
    } finally {
      setActionLoading(false);
    }
  }, [addNotification]);

  const handleDelete = useCallback(async (merchant) => {
    setActionLoading(true);
    try {
      await api.deleteMerchant(merchant.id);
      setMerchants(prev => prev.filter(m => m.id !== merchant.id));
      setSelectedMerchant(null);
      addNotification({ type: 'error', title: 'Merchant Deleted', message: `${merchant.name} has been removed.`, onClick: () => {}, action: true });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: error.message || 'Failed to delete merchant', onClick: () => {}, action: false });
    } finally {
      setActionLoading(false);
    }
  }, [addNotification]);

  const handleBulkApprove = async () => {
    const selectedCount = selectedMerchants.length;
    setActionLoading(true);
    try {
      for (const id of selectedMerchants) {
        await api.approveMerchant(id);
      }
      setMerchants(prev => prev.map(m => selectedMerchants.includes(m.id) ? { ...m, status: 'approved' } : m));
      setSelectedMerchants([]);
      addNotification({ type: 'success', title: 'Bulk Action Completed', message: `${selectedCount} merchants have been approved.`, onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Some merchants failed to approve', onClick: () => {}, action: false });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkReject = async () => {
    const selectedCount = selectedMerchants.length;
    setActionLoading(true);
    try {
      for (const id of selectedMerchants) {
        await api.rejectMerchant(id);
      }
      setMerchants(prev => prev.map(m => selectedMerchants.includes(m.id) ? { ...m, status: 'rejected' } : m));
      setSelectedMerchants([]);
      addNotification({ type: 'warning', title: 'Bulk Action Completed', message: `${selectedCount} merchants have been rejected.`, onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Some merchants failed to reject', onClick: () => {}, action: false });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelectMerchant = (id) => {
    setSelectedMerchants(prev => prev.includes(id) ? prev.filter(mId => mId !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedMerchants.length === filteredMerchants.length) {
      setSelectedMerchants([]);
    } else {
      setSelectedMerchants(filteredMerchants.map(m => m.id));
    }
  };

  const filteredMerchants = useMemo(() => {
    return merchants.filter(merchant => {
      const matchesSearch = merchant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           merchant.owner.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           merchant.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterStatus === 'all' || merchant.status === filterStatus;
      return matchesSearch && matchesFilter;
    });
  }, [merchants, searchQuery, filterStatus]);

  const stats = useMemo(() => ({
    all: merchants.length,
    pending: merchants.filter(m => m.status === 'pending').length,
    approved: merchants.filter(m => m.status === 'approved').length,
    rejected: merchants.filter(m => m.status === 'rejected').length
  }), [merchants]);

  const filterButtons = [
    { status: 'all', label: 'All', icon: Users, color: 'blue', count: stats.all },
    { status: 'pending', label: 'Pending', icon: Clock, color: 'orange', count: stats.pending },
    { status: 'approved', label: 'Approved', icon: CheckCircle, color: 'green', count: stats.approved },
    { status: 'rejected', label: 'Rejected', icon: XCircle, color: 'red', count: stats.rejected }
  ];

  const exportMerchants = () => {
    addNotification({ type: 'success', title: 'Export Complete', message: 'Merchant data exported to CSV successfully.', onClick: () => {}, action: false });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">Merchant Verification</h1>
          <p className="text-gray-300">Loading merchants...</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, type: '', merchant: null })}
        onConfirm={() => {
          if (confirmDialog.type === 'delete') handleDelete(confirmDialog.merchant);
          else if (confirmDialog.type === 'reject') handleReject(confirmDialog.merchant);
          else if (confirmDialog.type === 'approve') handleApprove(confirmDialog.merchant);
        }}
        title={confirmDialog.type === 'delete' ? 'Delete Merchant?' : confirmDialog.type === 'reject' ? 'Reject Application?' : 'Approve Merchant?'}
        message={
          confirmDialog.type === 'delete' 
            ? `Are you sure you want to permanently delete ${confirmDialog.merchant?.name}? This action cannot be undone.`
            : confirmDialog.type === 'reject'
            ? `Are you sure you want to reject ${confirmDialog.merchant?.name}'s application?`
            : `Are you sure you want to approve ${confirmDialog.merchant?.name}?`
        }
        confirmText={confirmDialog.type === 'delete' ? 'Delete' : confirmDialog.type === 'reject' ? 'Reject' : 'Approve'}
        type={confirmDialog.type === 'approve' ? 'warning' : 'danger'}
      />

      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Merchant Verification</h1>
            <p className="text-gray-300">Review and manage merchant applications</p>
          </div>
          <button onClick={exportMerchants} className="px-4 py-2 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-all flex items-center gap-2 font-semibold">
            <Download className="w-4 h-4" />
            Export Data
          </button>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {filterButtons.map(({ status, label, icon: Icon, color, count }) => {
          const isActive = filterStatus === status;
          const bgClass = isActive ? 
            (color === 'blue' ? 'border-blue-500 bg-blue-50 shadow-lg' :
             color === 'orange' ? 'border-orange-500 bg-orange-50 shadow-lg' :
             color === 'green' ? 'border-green-500 bg-green-50 shadow-lg' :
             'border-red-500 bg-red-50 shadow-lg') :
            'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md';
          
          const iconBgClass = color === 'blue' ? 'bg-blue-100' : color === 'orange' ? 'bg-orange-100' : color === 'green' ? 'bg-green-100' : 'bg-red-100';
          const iconColorClass = color === 'blue' ? 'text-blue-600' : color === 'orange' ? 'text-orange-600' : color === 'green' ? 'text-green-600' : 'text-red-600';
          
          return (
            <button key={status} onClick={() => setFilterStatus(status)} className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${bgClass}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${iconBgClass} rounded-lg flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${iconColorClass}`} />
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-600">{label}</p>
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search merchants by name, owner, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
          />
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedMerchants.length > 0 && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-900">{selectedMerchants.length} merchant(s) selected</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleBulkApprove} disabled={actionLoading} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center gap-2 disabled:opacity-50">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Approve All
            </button>
            <button onClick={handleBulkReject} disabled={actionLoading} className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all flex items-center gap-2 disabled:opacity-50">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Reject All
            </button>
            <button onClick={() => setSelectedMerchants([])} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Merchants Table */}
      {filteredMerchants.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <EmptyState icon={Users} title="No Merchants Found" description="No merchants match your search criteria. Try adjusting your filters or search query." />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <button onClick={toggleSelectAll} className="hover:bg-gray-200 p-1 rounded transition-colors">
                      {selectedMerchants.length === filteredMerchants.length && filteredMerchants.length > 0 ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
                    </button>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Merchant</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMerchants.map(merchant => (
                  <tr key={merchant.id} className="hover:bg-blue-50 transition-colors group">
                    <td className="px-6 py-4">
                      <button onClick={(e) => { e.stopPropagation(); toggleSelectMerchant(merchant.id); }} className="hover:bg-gray-200 p-1 rounded transition-colors">
                        {selectedMerchants.includes(merchant.id) ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => setSelectedMerchant(merchant)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold group-hover:scale-110 transition-transform">
                          {merchant.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{merchant.name}</p>
                          <p className="text-sm text-gray-500">{merchant.owner}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => setSelectedMerchant(merchant)}>
                      <p className="text-sm text-gray-900">{merchant.email}</p>
                      <p className="text-sm text-gray-500">{merchant.phone}</p>
                    </td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => setSelectedMerchant(merchant)}>
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">{merchant.category}</span>
                    </td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => setSelectedMerchant(merchant)}>
                      <p className="text-lg font-bold text-gray-900">{merchant.revenue}</p>
                    </td>
                    <td className="px-6 py-4 cursor-pointer" onClick={() => setSelectedMerchant(merchant)}>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        merchant.status === 'pending' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                        merchant.status === 'approved' ? 'bg-green-100 text-green-700 border border-green-200' :
                        'bg-red-100 text-red-700 border border-red-200'
                      }`}>
                        {merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {merchant.status === 'pending' && (
                          <>
                            <button onClick={() => setConfirmDialog({ isOpen: true, type: 'approve', merchant })} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all hover:scale-110" title="Approve">
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={() => setConfirmDialog({ isOpen: true, type: 'reject', merchant })} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all hover:scale-110" title="Reject">
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        <button onClick={() => setConfirmDialog({ isOpen: true, type: 'delete', merchant })} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all hover:scale-110" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Merchant Detail Modal */}
      {selectedMerchant && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedMerchant(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Merchant Details</h2>
                <p className="text-sm text-gray-500">Complete information and documents</p>
              </div>
              <button onClick={() => setSelectedMerchant(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Merchant Header */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                      {selectedMerchant.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{selectedMerchant.name}</h3>
                      <p className="text-gray-600">{selectedMerchant.owner}</p>
                    </div>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold border-2 ${
                    selectedMerchant.status === 'pending' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                    selectedMerchant.status === 'approved' ? 'bg-green-100 text-green-700 border-green-200' :
                    'bg-red-100 text-red-700 border-red-200'
                  }`}>
                    {selectedMerchant.status.charAt(0).toUpperCase() + selectedMerchant.status.slice(1)}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-colors">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Description</h4>
                <p className="text-gray-700 leading-relaxed">{selectedMerchant.description}</p>
              </div>

              {/* Contact Info */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-colors">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium text-gray-900">{selectedMerchant.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-sm font-medium text-gray-900">{selectedMerchant.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <MapPin className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-500">Location</p>
                      <p className="text-sm font-medium text-gray-900">{selectedMerchant.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <Building className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-xs text-gray-500">Category</p>
                      <p className="text-sm font-medium text-gray-900">{selectedMerchant.category}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-colors">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Documents</h4>
                <div className="grid grid-cols-1 gap-3">
                  {selectedMerchant.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc}</p>
                          <p className="text-xs text-gray-500">Verified</p>
                        </div>
                      </div>
                      <Download className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors cursor-pointer" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedMerchant.status === 'pending' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmDialog({ isOpen: true, type: 'reject', merchant: selectedMerchant })}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-all hover:shadow-lg transform hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Reject
                  </button>
                  <button
                    onClick={() => setConfirmDialog({ isOpen: true, type: 'approve', merchant: selectedMerchant })}
                    disabled={actionLoading}
                    className="flex-1 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold transition-all hover:shadow-lg transform hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Approve
                  </button>
                </div>
              )}

              <button
                onClick={() => setConfirmDialog({ isOpen: true, type: 'delete', merchant: selectedMerchant })}
                disabled={actionLoading}
                className="w-full py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-semibold transition-all hover:shadow-md disabled:opacity-50"
              >
                Delete Merchant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
// Notifications Center Component
const NotificationsCenter = ({ addNotification }) => {
  const [notificationsList, setNotificationsList] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const result = await api.getNotifications();
        if (result.success) {
          setNotificationsList(result.data);
        }
      } catch (error) {
        addNotification({ type: 'error', title: 'Error', message: 'Failed to load notifications', onClick: () => {}, action: false });
      } finally {
        setIsLoading(false);
      }
    };
    fetchNotifications();
  }, [addNotification]);

  const getNotificationIcon = (type) => {
    const icons = {
      merchant: { Icon: Users, color: 'bg-blue-100 text-blue-600' },
      payment: { Icon: DollarSign, color: 'bg-green-100 text-green-600' },
      system: { Icon: Settings, color: 'bg-gray-100 text-gray-600' },
      alert: { Icon: AlertTriangle, color: 'bg-red-100 text-red-600' }
    };
    return icons[type] || icons.system;
  };

  const markAsRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotificationsList(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotificationsList(prev => prev.map(n => ({ ...n, read: true })));
      addNotification({ type: 'success', title: 'All Marked as Read', message: 'All notifications marked as read successfully.', onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to mark all as read', onClick: () => {}, action: false });
    }
  };

  const deleteNotificationItem = async (id) => {
    try {
      await api.deleteNotification(id);
      setNotificationsList(prev => prev.filter(n => n.id !== id));
      addNotification({ type: 'info', title: 'Notification Deleted', message: 'Notification has been removed.', onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to delete notification', onClick: () => {}, action: false });
    }
  };

  const clearAll = async () => {
    try {
      await api.clearAllNotifications();
      setNotificationsList([]);
      addNotification({ type: 'info', title: 'Notifications Cleared', message: 'All notifications have been cleared.', onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to clear notifications', onClick: () => {}, action: false });
    }
  };

  const filteredNotifications = useMemo(() => {
    return notificationsList.filter(notif => {
      const matchesSearch = notif.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           notif.message.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filter === 'all' || 
                           (filter === 'unread' && !notif.read) ||
                           (filter === 'read' && notif.read) ||
                           notif.type === filter;
      return matchesSearch && matchesFilter;
    });
  }, [notificationsList, searchQuery, filter]);

  const stats = useMemo(() => ({
    all: notificationsList.length,
    unread: notificationsList.filter(n => !n.read).length,
    merchant: notificationsList.filter(n => n.type === 'merchant').length,
    payment: notificationsList.filter(n => n.type === 'payment').length,
    alert: notificationsList.filter(n => n.type === 'alert').length
  }), [notificationsList]);

  const filterOptions = [
    { value: 'all', label: 'All', icon: Bell, count: stats.all },
    { value: 'unread', label: 'Unread', icon: Circle, count: stats.unread },
    { value: 'merchant', label: 'Merchants', icon: Users, count: stats.merchant },
    { value: 'payment', label: 'Payments', icon: DollarSign, count: stats.payment },
    { value: 'alert', label: 'Alerts', icon: AlertTriangle, count: stats.alert }
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">Notifications</h1>
          <p className="text-gray-300">Loading notifications...</p>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map(i => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Notifications Center</h1>
            <p className="text-gray-300">Stay updated with all your notifications</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={markAllAsRead} className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm hover:bg-white/20 transition-all flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Mark All Read
            </button>
            <button onClick={clearAll} className="px-4 py-2 bg-red-500 rounded-lg text-white text-sm hover:bg-red-600 transition-all flex items-center gap-2 font-semibold">
              <Trash className="w-4 h-4" />
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {filterOptions.map(option => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`p-4 rounded-xl border-2 transition-all ${
              filter === option.value
                ? 'border-blue-500 bg-blue-50 shadow-lg'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <option.icon className={`w-5 h-5 ${filter === option.value ? 'text-blue-600' : 'text-gray-400'}`} />
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                filter === option.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {option.count}
              </span>
            </div>
            <p className={`text-sm font-medium ${filter === option.value ? 'text-blue-900' : 'text-gray-700'}`}>
              {option.label}
            </p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Notifications List */}
      {filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <EmptyState icon={Bell} title="No Notifications" description="You're all caught up! No notifications match your filters." />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map(notif => {
              const { Icon, color } = getNotificationIcon(notif.type);
              return (
                <div
                  key={notif.id}
                  className={`p-5 hover:bg-gray-50 transition-all cursor-pointer group ${!notif.read ? 'bg-blue-50/50' : ''}`}
                  onClick={() => markAsRead(notif.id)}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${color} group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <h4 className={`font-semibold ${!notif.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notif.title}
                        </h4>
                        {!notif.read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-1"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{notif.message}</p>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">{notif.time}</span>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                          {notif.category}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotificationItem(notif.id); }}
                      className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Settings Component
const SettingsPage = ({ addNotification }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    fullName: 'Admin User',
    email: 'admin@helpsum.com',
    phone: '+1 (555) 000-0000',
    role: 'Super Administrator'
  });

  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: true,
    loginAlerts: true
  });

  const [notifPrefs, setNotifPrefs] = useState({
    emailNotifications: true,
    pushNotifications: true,
    merchantApplications: true,
    paymentAlerts: true,
    systemUpdates: false,
    securityAlerts: true,
    weeklyReport: true
  });

  const [appearance, setAppearance] = useState({
    theme: 'light',
    language: 'en',
    timezone: 'UTC-5',
    dateFormat: 'MM/DD/YYYY'
  });

  const saveProfile = async () => {
    setIsLoading(true);
    try {
      await api.updateProfile(profileData);
      addNotification({ type: 'success', title: 'Profile Updated', message: 'Your profile has been updated successfully.', onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to update profile', onClick: () => {}, action: false });
    } finally {
      setIsLoading(false);
    }
  };

  const saveNotificationPreferences = () => {
    addNotification({ type: 'success', title: 'Preferences Saved', message: 'Notification preferences updated successfully.', onClick: () => {}, action: false });
  };

  const changePassword = async () => {
    if (securityData.newPassword !== securityData.confirmPassword) {
      addNotification({ type: 'error', title: 'Password Mismatch', message: 'New passwords do not match.', onClick: () => {}, action: false });
      return;
    }
    
    setIsLoading(true);
    try {
      await api.changePassword({
        currentPassword: securityData.currentPassword,
        newPassword: securityData.newPassword
      });
      setSecurityData({ ...securityData, currentPassword: '', newPassword: '', confirmPassword: '' });
      addNotification({ type: 'success', title: 'Password Changed', message: 'Your password has been updated successfully.', onClick: () => {}, action: false });
    } catch (error) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to change password', onClick: () => {}, action: false });
    } finally {
      setIsLoading(false);
    }
  };

  const saveAppearance = () => {
    addNotification({ type: 'success', title: 'Appearance Updated', message: 'Your appearance settings have been saved.', onClick: () => {}, action: false });
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'api', label: 'API & Integration', icon: Key }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Settings</h1>
        <p className="text-gray-300">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 space-y-1">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    activeTab === tab.id
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Profile Information</h2>
              
              <div className="space-y-5">
                <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-3xl">
                    A
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{profileData.fullName}</h3>
                    <p className="text-sm text-gray-600">{profileData.role}</p>
                    <button className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium">Change Avatar</button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData({ ...profileData, fullName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                    <input type="text" value={profileData.role} disabled className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500" />
                  </div>
                </div>

                <button
                  onClick={saveProfile}
                  disabled={isLoading}
                  className="w-full md:w-auto px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center justify-center gap-2 font-semibold disabled:opacity-50"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Change Password</h2>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={securityData.currentPassword}
                        onChange={(e) => setSecurityData({ ...securityData, currentPassword: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={securityData.newPassword}
                      onChange={(e) => setSecurityData({ ...securityData, newPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={securityData.confirmPassword}
                      onChange={(e) => setSecurityData({ ...securityData, confirmPassword: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <button
                    onClick={changePassword}
                    disabled={isLoading || !securityData.currentPassword || !securityData.newPassword}
                    className="w-full md:w-auto px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2 font-semibold disabled:opacity-50"
                  >
                    <Lock className="w-5 h-5" />
                    Update Password
                  </button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Security Options</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">Two-Factor Authentication</p>
                        <p className="text-sm text-gray-500">Add an extra layer of security</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSecurityData({ ...securityData, twoFactorEnabled: !securityData.twoFactorEnabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securityData.twoFactorEnabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securityData.twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Bell className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-gray-900">Login Alerts</p>
                        <p className="text-sm text-gray-500">Get notified of new login attempts</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSecurityData({ ...securityData, loginAlerts: !securityData.loginAlerts })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securityData.loginAlerts ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${securityData.loginAlerts ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Notification Preferences</h2>
              
              <div className="space-y-4">
                {Object.entries(notifPrefs).map(([key, value]) => {
                  const labels = {
                    emailNotifications: { title: 'Email Notifications', desc: 'Receive notifications via email' },
                    pushNotifications: { title: 'Push Notifications', desc: 'Receive browser push notifications' },
                    merchantApplications: { title: 'Merchant Applications', desc: 'New merchant application alerts' },
                    paymentAlerts: { title: 'Payment Alerts', desc: 'Payment and transaction notifications' },
                    systemUpdates: { title: 'System Updates', desc: 'Platform updates and maintenance' },
                    securityAlerts: { title: 'Security Alerts', desc: 'Security and login notifications' },
                    weeklyReport: { title: 'Weekly Report', desc: 'Receive weekly performance reports' }
                  };

                  return (
                    <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="font-medium text-gray-900">{labels[key].title}</p>
                        <p className="text-sm text-gray-500">{labels[key].desc}</p>
                      </div>
                      <button
                        onClick={() => setNotifPrefs({ ...notifPrefs, [key]: !value })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${value ? 'bg-blue-600' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={saveNotificationPreferences}
                className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2 font-semibold"
              >
                <Save className="w-5 h-5" />
                Save Preferences
              </button>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Appearance Settings</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Theme</label>
                  <div className="grid grid-cols-3 gap-3">
                    {['light', 'dark', 'auto'].map(theme => (
                      <button
                        key={theme}
                        onClick={() => setAppearance({ ...appearance, theme })}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          appearance.theme === theme ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-2">
                          {theme === 'light' && <Sun className="w-6 h-6" />}
                          {theme === 'dark' && <Moon className="w-6 h-6" />}
                          {theme === 'auto' && <Monitor className="w-6 h-6" />}
                          <span className="text-sm font-medium capitalize">{theme}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <select
                    value={appearance.language}
                    onChange={(e) => setAppearance({ ...appearance, language: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <select
                    value={appearance.timezone}
                    onChange={(e) => setAppearance({ ...appearance, timezone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="UTC-5">Eastern Time (UTC-5)</option>
                    <option value="UTC-6">Central Time (UTC-6)</option>
                    <option value="UTC-7">Mountain Time (UTC-7)</option>
                    <option value="UTC-8">Pacific Time (UTC-8)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                  <select
                    value={appearance.dateFormat}
                    onChange={(e) => setAppearance({ ...appearance, dateFormat: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>

                <button
                  onClick={saveAppearance}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2 font-semibold"
                >
                  <Save className="w-5 h-5" />
                  Save Appearance
                </button>
              </div>
            </div>
          )}

          {/* API Tab */}
          {activeTab === 'api' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">API Keys</h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Key className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">Production API Key</h4>
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Active</span>
                        </div>
                        <p className="text-sm text-gray-600 font-mono mb-3">hps_live_sk_a89f7g6h5j4k3l2m1n0p9q8r7s6t5u4v</p>
                        <div className="flex items-center gap-2">
                          <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">Copy</button>
                          <span className="text-gray-300">•</span>
                          <button className="text-sm text-red-600 hover:text-red-700 font-medium">Revoke</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Key className="w-5 h-5 text-gray-600 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">Test API Key</h4>
                          <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">Test</span>
                        </div>
                        <p className="text-sm text-gray-600 font-mono mb-3">hps_test_sk_x1y2z3a4b5c6d7e8f9g0h1i2j3k4l5m6</p>
                        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">Copy</button>
                      </div>
                    </div>
                  </div>
                </div>

                <button className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Generate New Key
                </button>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Webhooks</h2>
                <p className="text-sm text-gray-600 mb-6">Configure webhook endpoints to receive real-time event updates</p>
                
                <div className="space-y-3">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">https://api.example.com/webhooks/merchant</p>
                        <p className="text-sm text-gray-500">merchant.created, merchant.updated</p>
                      </div>
                      <MoreVertical className="w-5 h-5 text-gray-400 cursor-pointer" />
                    </div>
                  </div>
                </div>

                <button className="mt-4 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-all">
                  Add Webhook Endpoint
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Privacy Policy Component
const PrivacyPolicy = () => {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-gray-300">Your privacy matters to us</p>
      </div>
      
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="prose max-w-none space-y-6">
          <p className="text-gray-700">Your privacy matters to us. This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application and services.</p>
          
          <h3 className="text-xl font-bold text-gray-900">Information We Collect</h3>
          <p className="text-gray-700">We collect only the information necessary to provide our services, including:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Phone number (used for account creation and verification)</li>
            <li>One-Time Password (OTP) for secure login and authentication</li>
            <li>Location data to connect consumers with nearby service providers</li>
            <li>Booking details and service history</li>
            <li>In-app chat messages between consumers and merchants</li>
            <li>Ratings and reviews</li>
            <li>Basic device and usage information for app performance and security</li>
          </ul>
          <p className="text-gray-700 font-bold">⚠️ We do not collect email addresses.</p>

          <h3 className="text-xl font-bold text-gray-900">How We Use Your Information</h3>
          <p className="text-gray-700">Your information is used to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Verify user identity through phone number and OTP</li>
            <li>Create and manage user accounts</li>
            <li>Connect consumers with merchants</li>
            <li>Enable bookings and in-app communication</li>
            <li>Improve app functionality and user experience</li>
            <li>Maintain platform safety and prevent misuse</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h3 className="text-xl font-bold text-gray-900">Data Sharing</h3>
          <p className="text-gray-700">We do not sell or rent your personal data. Information may be shared only:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Between consumers and merchants involved in a booking</li>
            <li>With trusted third-party services required to operate the app</li>
            <li>When required by law or legal process</li>
          </ul>

          <h3 className="text-xl font-bold text-gray-900">Data Security</h3>
          <p className="text-gray-700">We implement reasonable security measures to protect your data from unauthorized access, misuse, or loss.</p>
          
          <h3 className="text-xl font-bold text-gray-900">User Rights</h3>
          <p className="text-gray-700">You have the right to:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Access and update your personal information</li>
            <li>Request deletion of your account and associated data at any time</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Changes to This Policy</h3>
          <p className="text-gray-700">We may update this Privacy Policy periodically. Continued use of the app indicates acceptance of the updated policy.</p>
        </div>
      </div>
    </div>
  );
};

// Terms of Service Component
const TermsOfService = () => {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl p-6 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Terms and Conditions</h1>
        <p className="text-gray-300">By using this application, you agree to these Terms and Conditions.</p>
      </div>
      
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="prose max-w-none space-y-6">
          <h3 className="text-xl font-bold text-gray-900">Account Registration</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Accounts are created using a phone number and OTP verification</li>
            <li>Users must provide accurate and up-to-date information</li>
            <li>You are responsible for all activity under your account</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Use of the Platform</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>The app is a platform that connects consumers with service providers</li>
            <li>We do not directly provide services</li>
            <li>Merchants are responsible for service delivery, quality, and completion</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Bookings</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Booking requests are subject to merchant acceptance and availability</li>
            <li>Any disputes related to services must be resolved between the consumer and merchant</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Communication</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>In-app chat is provided for service-related communication only</li>
            <li>Abuse, spam, or inappropriate behavior may result in account suspension</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Ratings and Reviews</h3>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Ratings and reviews must be honest and respectful</li>
            <li>We reserve the right to remove inappropriate or misleading content</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Account Termination</h3>
          <p className="text-gray-700">We may suspend or terminate accounts that:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>Violate these terms</li>
            <li>Engage in fraud, abuse, or misuse of the platform</li>
          </ul>
          
          <h3 className="text-xl font-bold text-gray-900">Limitation of Liability</h3>
          <p className="text-gray-700">We are not responsible for losses, damages, or disputes arising from services provided by merchants or user interactions.</p>
          
          <h3 className="text-xl font-bold text-gray-900">Changes to Terms</h3>
          <p className="text-gray-700">We may update these Terms and Conditions at any time. Continued use of the app means you accept the updated terms.</p>
        </div>
      </div>
    </div>
  );
};

// Delete Account Component
const DeleteAccount = ({ addNotification }) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const handleDeleteRequest = () => {
    setIsConfirming(true);
  };
  
  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      addNotification({
        type: 'success',
        title: 'Account Deleted',
        message: 'Your account has been successfully deleted.',
        onClick: () => {},
        action: false
      });
      
      setIsConfirming(false);
      setPassword('');
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to delete account. Please try again.',
        onClick: () => {},
        action: false
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-800 to-red-900 rounded-2xl p-6 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Delete Account</h1>
        <p className="text-gray-300">This action is permanent and cannot be undone</p>
      </div>
      
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
        <div className="space-y-6">
          <div className="prose max-w-none">
            <h3 className="text-xl font-bold text-gray-900">How to Delete Your Account</h3>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700">
              <li>Log in to the app using your phone number</li>
              <li>Go to Profile &gt; Settings</li>
              <li>Select Delete Account</li>
              <li>Confirm your request</li>
            </ol>

            <h3 className="text-xl font-bold text-gray-900 mt-6">What Happens After Deletion</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Your phone number will be removed from our system</li>
              <li>Your booking history, chat messages, and ratings will be permanently deleted</li>
              <li>Account deletion is permanent and cannot be undone</li>
            </ul>
            
            <p className="text-gray-700 mt-6">If you are unable to access your account and wish to request deletion, please contact support through the app.</p>
          </div>
          
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-red-700">
            <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Warning: Account Deletion
            </h2>
            <p className="text-sm">
              Deleting your account will permanently remove all your data from our system. 
              This includes your profile, settings, and all associated information. This action cannot be reversed.
            </p>
          </div>
          
          {!isConfirming ? (
            <div className="text-center">
              <button
                onClick={handleDeleteRequest}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold"
              >
                Request Account Deletion
              </button>
            </div>
          ) : (
            <div className="space-y-4 border-2 border-red-100 p-6 rounded-xl bg-red-50">
              <h3 className="font-bold text-gray-900">Confirm Account Deletion</h3>
              <p className="text-sm text-gray-700">Please enter your password to confirm account deletion:</p>
              
              <div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setIsConfirming(false)}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={!password || isLoading}
                  className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  Permanently Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
// Router Component to handle URL changes
const AppRouter = ({ addNotification }) => {
  return (
    <Routes>
      <Route path="/" element={<Dashboard addNotification={addNotification} />} />
      <Route path="/dashboard" element={<Dashboard addNotification={addNotification} />} />
      <Route path="/merchants" element={<MerchantVerification addNotification={addNotification} />} />
      <Route path="/transactions" element={<TransactionManagement addNotification={addNotification} />} />
      <Route path="/support" element={<SupportTicketing addNotification={addNotification} />} />
      <Route path="/audit-logs" element={<AuditLogs addNotification={addNotification} />} />
      <Route path="/notifications" element={<NotificationsCenter addNotification={addNotification} />} />
      <Route path="/settings" element={<SettingsPage addNotification={addNotification} />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/delete-account" element={<DeleteAccount addNotification={addNotification} />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// Main App Component
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      api.logout();
      setIsLoggedIn(false);
    } else {
      setIsLoggedIn(true);
    }
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { ...notification, id }]);
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    api.logout();
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <BrowserRouter>
      <div className="flex h-screen bg-gray-50">
        {/* Toast Notifications */}
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
        
        {/* Sidebar */}
        <Sidebar 
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          onLogout={handleLogout}
        />
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile Header */}
          <div className="md:hidden bg-white border-b border-gray-200 p-4">
            <button onClick={() => setSidebarOpen(true)} className="text-gray-600 hover:bg-gray-100 p-2 rounded-lg transition-colors">
              <Menu className="w-6 h-6" />
            </button>
          </div>
          
          {/* Page Content */}
          <main className="flex-1 overflow-y-auto p-6">
            <AppRouter addNotification={addNotification} />
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}

export default App;
